import { Box, Paper, Tab, Tabs, TextField, Typography } from '@mui/material'
import I18n from 'i18n-js'
import { Redirect, Route, Switch, useHistory } from 'react-router'

import LinkChild from '@src/lib/LinkChild'
import MenuItemWithCheckbox from '@src/lib/MenuItemWithCheckbox'
import useCurrentRetailerAccount from '@src/lib/useCurrentRetailerAccount'
import useOnRetailerAccountChange from '@src/lib/useOnRetailerAccountChange'

import RetailerSettingsBrandSettingsAutomations from './RetailerSettingsBrandSettingsAutomations'
import RetailerSettingsBrandSettingsLocations from './RetailerSettingsBrandSettingsLocations'
import RetailerSettingsBrandSettingsQuestions from './RetailerSettingsBrandSettingsQuestions'
import RetailerSettingsBrandSettingsWebsite from './RetailerSettingsBrandSettingsWebsite'
import retailerSettingsPaths from './retailerSettingsPaths'

const RetailerSettingsBrandSettingsLayout: React.FC = () => {
  const params = retailerSettingsPaths.brandSettings.useParams()
  const brandSlug = params.brandSlug
  const { ensureCurrentRetailerAccount } = useCurrentRetailerAccount()
  const history = useHistory()

  // When the RetailerAccount changes, check if they have access to the
  // selected brand.
  // If they don't, redirect back to the root brand settings route (which will
  // then select their first brand if they have any.)
  useOnRetailerAccountChange((retailerAccount) => {
    const doesHaveBrand = retailerAccount.retailers?.some((retailer) => {
      return retailer.brand?.slug === brandSlug
    })

    if (!doesHaveBrand) {
      const newLocation = retailerSettingsPaths.brandSettings.build({
        retailerAccountSlug: retailerAccount.slug || '',
      })

      history.replace(newLocation)
    }
  })

  let selectedTab = ''
  if (retailerSettingsPaths.brandSettingsAutomations.match()) {
    selectedTab = 'brandSettingsAutomations'
  } else if (retailerSettingsPaths.brandSettingsLocations.match()) {
    selectedTab = 'brandSettingsLocations'
  } else if (retailerSettingsPaths.brandSettingsQuestions.match()) {
    selectedTab = 'brandSettingsQuestions'
  } else if (retailerSettingsPaths.brandSettingsWebsite.match()) {
    selectedTab = 'brandSettingsWebsite'
  }

  function getNewLocationForBrandSlug(brandSlug: string) {
    let newLocation: string | undefined

    if (brandSlug === '-1') {
      newLocation = retailerSettingsPaths.brandSettings.build({
        retailerAccountSlug: params.retailerAccountSlug,
      })
    } else {
      switch (selectedTab) {
        case 'brandSettingsAutomations':
          newLocation = retailerSettingsPaths.brandSettingsAutomations.build({
            retailerAccountSlug: params.retailerAccountSlug,
            brandSlug,
          })
          break
        case 'brandSettingsLocations':
          newLocation = retailerSettingsPaths.brandSettingsLocations.build({
            retailerAccountSlug: params.retailerAccountSlug,
            brandSlug,
          })
          break
        case 'brandSettingsWebsite':
          newLocation = retailerSettingsPaths.brandSettingsWebsite.build({
            retailerAccountSlug: params.retailerAccountSlug,
            brandSlug,
          })
          break
        case 'brandSettingsQuestions':
        case '':
          newLocation = retailerSettingsPaths.brandSettingsQuestions.build({
            retailerAccountSlug: params.retailerAccountSlug,
            brandSlug,
          })
          break
      }
    }

    return newLocation
  }

  if (!ensureCurrentRetailerAccount.retailers?.length) {
    return (
      <>
        <Box textAlign="center" paddingY={4}>
          <Typography variant="h6" color="text.secondary">
            {I18n.t('retailerSettings.brandSettings.noBrands', {
              retailerAccountName: ensureCurrentRetailerAccount.name,
            })}
          </Typography>
        </Box>
      </>
    )
  }

  if (!brandSlug) {
    const firstBrand = ensureCurrentRetailerAccount.retailers?.[0]?.brand
    const newLocation = getNewLocationForBrandSlug(
      // Since we've already checked if there's no brands above, this should be
      // safe.
      firstBrand?.slug || '',
    )

    return <Redirect to={newLocation || ''} />
  }

  return (
    <>
      <Typography variant="h6" gutterBottom>
        {I18n.t('retailerSettings.brandSettings.title')}
      </Typography>

      <TextField
        select
        value={brandSlug}
        fullWidth
        margin="none"
        sx={{ marginBottom: 2 }}
        onChange={(event) => {
          const newLocation = getNewLocationForBrandSlug(event.target.value)

          if (newLocation) {
            history.push(newLocation)
          }
        }}
      >
        {ensureCurrentRetailerAccount.retailers?.map((x) => {
          return (
            <MenuItemWithCheckbox key={x.id} value={x.brand?.slug || ''}>
              {x.brand?.name}
            </MenuItemWithCheckbox>
          )
        })}
      </TextField>

      {params.brandSlug ? (
        <>
          <Paper>
            <Tabs value={selectedTab}>
              <LinkChild
                to={retailerSettingsPaths.brandSettingsQuestions.build({
                  retailerAccountSlug: params.retailerAccountSlug,
                  brandSlug: params.brandSlug,
                })}
                value="brandSettingsQuestions"
              >
                <Tab
                  label={I18n.t(
                    'retailerSettings.brandSettings.questions.title',
                  )}
                />
              </LinkChild>

              <LinkChild
                to={retailerSettingsPaths.brandSettingsLocations.build({
                  retailerAccountSlug: params.retailerAccountSlug,
                  brandSlug: params.brandSlug,
                })}
                value="brandSettingsLocations"
              >
                <Tab
                  label={I18n.t(
                    'retailerSettings.brandSettings.locations.title',
                  )}
                />
              </LinkChild>

              <LinkChild
                to={retailerSettingsPaths.brandSettingsWebsite.build({
                  retailerAccountSlug: params.retailerAccountSlug,
                  brandSlug: params.brandSlug,
                })}
                value="brandSettingsWebsite"
              >
                <Tab
                  label={I18n.t('retailerSettings.brandSettings.website.title')}
                />
              </LinkChild>

              <LinkChild
                to={retailerSettingsPaths.brandSettingsAutomations.build({
                  retailerAccountSlug: params.retailerAccountSlug,
                  brandSlug: params.brandSlug,
                })}
                value="brandSettingsAutomations"
              >
                <Tab
                  label={I18n.t(
                    'retailerSettings.brandSettings.automations.title',
                  )}
                />
              </LinkChild>
            </Tabs>
          </Paper>

          <Box marginTop={4}>
            <Switch>
              <Route
                path={retailerSettingsPaths.brandSettingsAutomations.path}
                component={RetailerSettingsBrandSettingsAutomations}
              />
              <Route
                path={retailerSettingsPaths.brandSettingsLocations.path}
                component={RetailerSettingsBrandSettingsLocations}
              />
              <Route
                path={retailerSettingsPaths.brandSettingsWebsite.path}
                component={RetailerSettingsBrandSettingsWebsite}
              />
              <Route
                path={retailerSettingsPaths.brandSettingsQuestions.path}
                component={RetailerSettingsBrandSettingsQuestions}
              />
            </Switch>
          </Box>
        </>
      ) : undefined}
    </>
  )
}

export default RetailerSettingsBrandSettingsLayout
